import React, { useState, useEffect, useCallback } from 'react';
import { MagnifyingGlassIcon, EyeIcon, PencilIcon, ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon, PlusIcon } from '@heroicons/react/24/outline';
import AdminViewModal from './AdminViewModal';
import AdminEditModal from './AdminEditModal';
import AdminCreateModal from './AdminCreateModal';
import ConfirmModal from './ConfirmModal';
import { apiService, Admin } from '../services/api';

interface AdminManagementProps {
  onBack: () => void;
}

// API state
interface ApiState {
  admins: Admin[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  } | null;
  loading: boolean;
  error: string | null;
}

const AdminManagement: React.FC<AdminManagementProps> = ({ onBack }) => {
  const [apiState, setApiState] = useState<ApiState>({
    admins: [],
    pagination: null,
    loading: false,
    error: null,
  });
  const [selectedAdmin, setSelectedAdmin] = useState<Admin | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    visible: boolean;
    admin: Admin | null;
  }>({
    visible: false,
    admin: null,
  });

  // State for pagination and search
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [roleFilter, setRoleFilter] = useState<string>('');

  const loadAdmins = useCallback(async () => {
    setApiState(prev => ({ ...prev, loading: true, error: null }));
    try {
      // If sorting by isActive, don't send sortBy to API (will sort client-side)
      const shouldSortClientSide = sortBy === 'isActive';
      
      // Handle isActive filter: convert string to boolean or undefined for 'All'
      let isActiveParam: boolean | undefined = undefined;
      if (statusFilter === 'true') {
        isActiveParam = true;
      } else if (statusFilter === 'false') {
        isActiveParam = false;
      }
      
      const params: {
        page: number;
        limit: number;
        search?: string;
        sortBy?: 'id' | 'name' | 'email' | 'role' | 'createdAt' | 'lastLogin';
        sortOrder?: 'asc' | 'desc';
        isActive?: boolean;
        role?: string;
      } = {
        page: currentPage,
        limit: pageSize,
        search: searchTerm || undefined,
      };
      
      // Only add sortBy and sortOrder if not sorting by isActive
      if (!shouldSortClientSide) {
        params.sortBy = sortBy as 'id' | 'name' | 'email' | 'role' | 'createdAt' | 'lastLogin';
        params.sortOrder = sortOrder;
      }
      
      // Only add isActive if it has a value
      if (isActiveParam !== undefined) {
        params.isActive = isActiveParam;
      }
      
      if (roleFilter) {
        params.role = roleFilter;
      }

      const response = await apiService.getAdmins(params as any);
      
      // Client-side sorting for isActive
      let sortedAdmins = response.admins || [];
      if (shouldSortClientSide && sortedAdmins.length > 0) {
        sortedAdmins = [...sortedAdmins].sort((a, b) => {
          if (sortOrder === 'asc') {
            return a.isActive === b.isActive ? 0 : a.isActive ? 1 : -1;
          } else {
            return a.isActive === b.isActive ? 0 : a.isActive ? -1 : 1;
          }
        });
      }
      
      setApiState({
        admins: sortedAdmins,
        pagination: response.pagination,
        loading: false,
        error: null,
      });
    } catch (error) {
      setApiState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'Failed to load admins',
      }));
    }
  }, [currentPage, pageSize, searchTerm, sortBy, sortOrder, statusFilter, roleFilter]);

  useEffect(() => {
    loadAdmins();
  }, [loadAdmins]);

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    setCurrentPage(1); 
  };

  const handleSort = (field: string) => {
    const allowedFields = ['id', 'name', 'email', 'role', 'createdAt', 'lastLogin', 'isActive'];
    if (!allowedFields.includes(field)) {
      return; 
    }
    
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing page size
  };

  const handleStatusFilterChange = (status: string) => {
    setStatusFilter(status);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleRoleFilterChange = (role: string) => {
    setRoleFilter(role);
    setCurrentPage(1); // Reset to first page when filtering
  };

  const handleViewAdmin = (admin: Admin) => {
    setSelectedAdmin(admin);
    setIsViewModalOpen(true);
  };

  const handleEditAdmin = (admin: Admin) => {
    setSelectedAdmin(admin);
    setIsEditModalOpen(true);
  };

  const confirmToggleAdminStatus = async () => {
    if (!confirmModal.admin) return;
    
    setIsLoading(true);
    setApiState(prev => ({ ...prev, error: null })); // Clear previous errors
    try {
      await apiService.toggleAdminStatus(confirmModal.admin.id);
      // Reload admins to get updated data
      loadAdmins();
      setConfirmModal({ visible: false, admin: null });
    } catch (error) {
      console.error('Error toggling admin status:', error);
      setApiState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to toggle admin status',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAdmin = async (updatedAdmin: Admin) => {
    setIsLoading(true);
    setApiState(prev => ({ ...prev, error: null })); // Clear previous errors
    try {
      await apiService.updateAdmin(updatedAdmin.id, updatedAdmin);
      // Reload admins to get updated data
      loadAdmins();
      setIsEditModalOpen(false);
      setSelectedAdmin(null);
    } catch (error) {
      console.error('Error updating admin:', error);
      setApiState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to update admin',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateAdmin = async (adminData: { email: string; name: string; password: string; role: 'SUPER_ADMIN' | 'ADMIN' | 'STAFF' }) => {
    setIsLoading(true);
    setApiState(prev => ({ ...prev, error: null })); // Clear previous errors
    try {
      await apiService.createAdmin(adminData);
      // Reload admins to get updated data
      loadAdmins();
      setIsCreateModalOpen(false);
    } catch (error) {
      console.error('Error creating admin:', error);
      setApiState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to create admin',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  const getSortIcon = (field: string) => {
    if (sortBy !== field) return <ChevronUpIcon className="w-4 h-4 text-gray-400" />;
    return sortOrder === 'asc' ? <ChevronUpIcon className="w-4 h-4 text-gray-600" /> : <ChevronDownIcon className="w-4 h-4 text-gray-600" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadge = (isActive: boolean) => {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
        }`}>
        {isActive ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getRoleBadge = (role: string) => {
    const colors = {
      SUPER_ADMIN: 'bg-purple-100 text-purple-800',
      ADMIN: 'bg-blue-100 text-blue-800',
      STAFF: 'bg-green-100 text-green-800'
    };
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors[role as keyof typeof colors]}`}>
        {role.replace('_', ' ')}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={onBack}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Admin Management</h1>
        </div>
        <div className="text-sm text-gray-500">
          Total Admins: {apiState.pagination?.total || 0}
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-imola-primary border border-transparent rounded-md hover:bg-imola-primary/80 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-imola-primary"
        >
          <PlusIcon className="w-4 h-4 mr-2" />Create Admin
        </button>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center space-x-4">
          <div className="flex-1">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Search admins by name or email..."
                value={searchTerm}
                onChange={(e) => handleSearch(e.target.value)}
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-imola-primary focus:border-imola-primary sm:text-sm"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Status:</label>
            <select
              value={statusFilter}
              onChange={(e) => handleStatusFilterChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="">All</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Role:</label>
            <select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value="">All</option>
              <option value="SUPER_ADMIN">Super Admin</option>
              <option value="ADMIN">Admin</option>
              <option value="STAFF">Staff</option>
            </select>
          </div>
          <div className="flex items-center space-x-2">
            <label className="text-sm text-gray-600">Page Size:</label>
            <select
              value={pageSize}
              onChange={(e) => handlePageSizeChange(Number(e.target.value))}
              className="border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-imola-primary"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {apiState.error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800 text-left">Error</h3>
              <div className="mt-2 text-sm text-red-700">{apiState.error}</div>
            </div>
          </div>
        </div>
      )}

      {/* Admins Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Admin
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('role')}
                    className="flex items-center space-x-1 hover:text-gray-700 focus:outline-none"
                  >
                    <span>Role</span>
                    {getSortIcon('role')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('isActive')}
                    className="flex items-center space-x-1 hover:text-gray-700 focus:outline-none"
                  >
                    <span>Status</span>
                    {getSortIcon('isActive')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('createdAt')}
                    className="flex items-center space-x-1 hover:text-gray-700 focus:outline-none"
                  >
                    <span>Created</span>
                    {getSortIcon('createdAt')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <button
                    onClick={() => handleSort('lastLogin')}
                    className="flex items-center space-x-1 hover:text-gray-700 focus:outline-none"
                  >
                    <span>Last Login</span>
                    {getSortIcon('lastLogin')}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {apiState.loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    Loading...
                  </td>
                </tr>
              ) : apiState.admins.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                    No admins found
                  </td>
                </tr>
              ) : (
                apiState.admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center mr-3">
                          <span className="text-sm font-medium text-gray-600">
                            {admin.name.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 text-left">{admin.name}</div>
                          <div className="text-sm text-gray-500 text-left">{admin.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      {getRoleBadge(admin.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-left">
                      {getStatusBadge(admin.isActive)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                      {formatDate(admin.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-left">
                      {admin.lastLogin ? formatDate(admin.lastLogin) : '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => handleViewAdmin(admin)}
                          className="text-blue-600 hover:text-blue-800 transition-colors"
                          title="View"
                        >
                          <EyeIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleEditAdmin(admin)}
                          className="text-green-600 hover:text-green-800 transition-colors"
                          title="Edit"
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {apiState.pagination && apiState.pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === apiState.pagination!.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{(currentPage - 1) * pageSize + 1}</span> to{' '}
                  <span className="font-medium">
                    {Math.min(currentPage * pageSize, apiState.pagination!.total)}
                  </span>{' '}
                  of <span className="font-medium">{apiState.pagination!.total}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="w-4 h-4" />
                  </button>
                  {Array.from(Array(Math.min(5, apiState.pagination!.totalPages)), (_, i) => {
                    const page = i + 1;
                    return (
                      <button
                        key={page}
                        onClick={() => handlePageChange(page)}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${page === currentPage
                          ? 'z-10 bg-imola-primary border-imola-primary text-white'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                          }`}
                      >
                        {page}
                      </button>
                    );
                  })}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === apiState.pagination!.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="w-4 h-4" />
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* View Admin Modal */}
      {isViewModalOpen && selectedAdmin && (
        <AdminViewModal
          admin={selectedAdmin}
          onClose={() => {
            setIsViewModalOpen(false);
            setSelectedAdmin(null);
          }}
        />
      )}

      {/* Edit Admin Modal */}
      {isEditModalOpen && selectedAdmin && (
        <AdminEditModal
          admin={selectedAdmin}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedAdmin(null);
          }}
          onSave={handleSaveAdmin}
          isLoading={isLoading}
          error={apiState.error}
        />
      )}

      {/* Create Admin Modal */}
      {isCreateModalOpen && (
        <AdminCreateModal
          onClose={() => {
            setIsCreateModalOpen(false);
          }}
          onSave={handleCreateAdmin}
          isLoading={isLoading}
          error={apiState.error}
        />
      )}

      {/* Confirm Modal */}
      {confirmModal.visible && confirmModal.admin && (
        <ConfirmModal
          visible={confirmModal.visible}
          title="Confirm Action"
          message={`Are you sure you want to ${confirmModal.admin.isActive ? 'deactivate' : 'activate'} ${confirmModal.admin.name}?`}
          confirmText={confirmModal.admin.isActive ? 'Deactivate' : 'Activate'}
          cancelText="Cancel"
          confirmButtonClass={confirmModal.admin.isActive ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}
          onConfirm={confirmToggleAdminStatus}
          onCancel={() => setConfirmModal({ visible: false, admin: null })}
        />
      )}
    </div>
  );
};

export default AdminManagement; 